import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";

import { modalConfig } from "../../../../config/ui/modals/modal-config";

export interface ModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Modal title */
  title?: string;
  /** Modal size */
  size?: keyof typeof modalConfig.sizes;
  /** Whether to show close button in header */
  showCloseButton?: boolean;
  /** Whether modal can be closed by clicking overlay */
  closeOnOverlayClick?: boolean;
  /** Whether modal can be closed with Escape key */
  closeOnEscape?: boolean;
  /** Custom class name */
  className?: string;
  /** Custom styles */
  style?: React.CSSProperties;
  /** Children content */
  children: React.ReactNode;
}

/**
 * Simple Modal Component for System Settings
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  size = "medium",
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = "",
  style = {},
  children,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Handle Escape key
  useEffect(() => {
    if (!closeOnEscape || !isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeOnEscape, isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === overlayRef.current) {
      onClose();
    }
  };

  const handleCloseClick = () => {
    onClose();
  };

  const modalContent = (
    <div
      ref={overlayRef}
      style={{
        ...modalConfig.overlay.default,
        zIndex: modalConfig.zIndex.overlay,
      }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={className}
        style={{
          ...modalConfig.container.default,
          ...modalConfig.sizes[size],
          ...style,
          zIndex: modalConfig.zIndex.modal,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {(title || showCloseButton) && (
          <div style={modalConfig.header.default}>
            {title && <h2 style={modalConfig.header.title}>{title}</h2>}

            {showCloseButton && (
              <button
                style={{
                  position: "absolute",
                  top: "20px",
                  right: "20px",
                  background: "none",
                  border: "none",
                  fontSize: "18px",
                  cursor: "pointer",
                  padding: "4px",
                  borderRadius: "4px",
                  color: "#6b7280",
                }}
                onClick={handleCloseClick}
                aria-label="Close modal"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div style={modalConfig.body.default}>{children}</div>
      </div>
    </div>
  );

  // Render in portal
  return ReactDOM.createPortal(modalContent, document.body);
};

/**
 * ModalButton Component - Styled button for modal actions
 */
export interface ModalButtonProps {
  /** Button variant/style */
  variant?: "primary" | "secondary" | "danger";
  /** Click handler */
  onClick?: () => void;
  /** Button text/children */
  children: React.ReactNode;
  /** Disabled state */
  disabled?: boolean;
  /** Custom className */
  className?: string;
  /** Button type */
  type?: "button" | "submit" | "reset";
}

export const ModalButton: React.FC<ModalButtonProps> = ({
  variant = "primary",
  onClick,
  children,
  disabled = false,
  className = "",
  type = "button",
}) => {
  const variantStyles = {
    primary: {
      backgroundColor: "#3b82f6",
      color: "white",
      border: "none",
    },
    secondary: {
      backgroundColor: "#e5e7eb",
      color: "#374151",
      border: "none",
    },
    danger: {
      backgroundColor: "#ef4444",
      color: "white",
      border: "none",
    },
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{
        ...variantStyles[variant],
        padding: "8px 16px",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "500",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s ease",
      }}
    >
      {children}
    </button>
  );
};

export default Modal;
